/**
 * One dig report's card — used on the dig page and on the report page.
 *
 * Summary (PF-3b, ReportSummary): the approved summary; on a draft, the AI's
 * pending draft for approval and a person's own text. Issued: read-only.
 *
 * Draft: title and comments are editable, the DRAFT PDF can be opened, and it
 * can be issued (PF_DISPATCH) — the API issues only once the dig is backfilled
 * (`dig_not_backfilled`) and issuing moves the dig to `reported`. Issued: the
 * number, who signed it and when, and the stored PDF. Every refusal is drawn
 * inline in the refusal style.
 *
 * THE PDF TAB IS CLAIMED BEFORE THE AWAIT (lib/blob-tab): a tab opened after a
 * multi-second render is silently refused by the browser.
 */
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FileText } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../../api/client';
import { useDeletePfReport, useIssuePfReport, useUpdatePfReport } from '../../../hooks/usePfDigs';
import { claimBlobTab, POPUP_BLOCKED_MESSAGE, showBlobInTab } from '../../../lib/blob-tab';
import { formatInstant } from '../../../lib/calendar-date';
import { pfDigRefusal, pfReportStatusSkin } from '../../../lib/pf-digs';
import { blankToNull } from '../../../lib/pipeline-facility';
import type { PfReport, PfReportAiDraft } from '../../../types/pf-digs';
import { PF_INPUT, PF_PRIMARY, PF_SECONDARY, PfBadge, PfField, PfFormError, PfSpec } from '../PfBits';
import { PfInlineConfirm } from './DigBits';
import { PfReportSummary } from './ReportSummary';

/** Open a report's PDF in a new tab; returns a sentence on failure, null on success. */
export async function openPfReportPdf(reportId: string, win: Window | null): Promise<string | null> {
  if (!win) return POPUP_BLOCKED_MESSAGE;
  try {
    const res = await api.pipelineFacility.reports.pdf(reportId);
    showBlobInTab(win, res.data);
    return null;
  } catch (err) {
    win.close();
    return pfDigRefusal(err, 'The PDF could not be made.');
  }
}


export function ReportPanelBody({
  report,
  canWrite,
  showOpenLink,
  timeZone,
}: {
  /** The detail's `aiDraft` (PF-3b) rides along when the caller has it. */
  report: PfReport & { aiDraft?: PfReportAiDraft | null };
  canWrite: boolean;
  showOpenLink?: boolean;
  /** The tenant's zone (meta.timeZone) — an issue time is an instant, shown in the company's zone, never the viewer's. */
  timeZone: string | null | undefined;
}) {
  const navigate = useNavigate();
  const update = useUpdatePfReport();
  const issue = useIssuePfReport();
  const del = useDeletePfReport();
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(report.title ?? '');
  const [comments, setComments] = useState(report.comments ?? '');
  const [error, setError] = useState<string | null>(null);
  const [issuing, setIssuing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  const draft = report.status === 'draft';

  const openPdf = async () => {
    setError(null);
    const win = claimBlobTab();
    setPdfBusy(true);
    const problem = await openPfReportPdf(report.id, win);
    setPdfBusy(false);
    if (problem) setError(problem);
  };

  return (
    <div className="space-y-3 px-4 py-4" data-testid="pf-report-card" data-status={report.status}>
      <dl className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <PfSpec label="Report" value={report.reportNumber || 'Not numbered until issued'} mono />
        <PfSpec label="Status" value={<PfBadge skin={pfReportStatusSkin(report.status)} value={report.status} />} />
        <PfSpec label="Issued" value={formatInstant(report.issuedAt, timeZone) ?? '—'} />
        <PfSpec label="Signed by" value={report.issuedSignatoryName || '—'} />
      </dl>
      {!editing && (
        <>
          <p className="text-sm text-fieldiq-text"><span className="font-bold">Title: </span>{report.title || '—'}</p>
          <p className="whitespace-pre-wrap text-sm text-fieldiq-text"><span className="font-bold">Comments: </span>{report.comments || '—'}</p>
        </>
      )}
      {!editing && <PfReportSummary report={report} canWrite={canWrite} timeZone={timeZone} />}
      {!issuing && !deleting && <PfFormError message={error} />}
      {editing && (
        <div className="space-y-3" data-testid="pf-report-edit">
          <PfField label="Title">
            <input className={PF_INPUT} aria-label="Report title" value={title} onChange={(e) => setTitle(e.target.value)} />
          </PfField>
          <PfField label="Comments">
            <textarea className={`${PF_INPUT} h-28 py-2`} aria-label="Report comments" value={comments} onChange={(e) => setComments(e.target.value)} />
          </PfField>
          <div className="flex justify-end gap-2">
            <button type="button" className={PF_SECONDARY} onClick={() => setEditing(false)} disabled={update.isPending}>
              Cancel
            </button>
            <button
              type="button"
              className={PF_PRIMARY}
              disabled={update.isPending}
              onClick={() => {
                setError(null);
                update.mutate(
                  { id: report.id, body: { title: blankToNull(title), comments: blankToNull(comments) } },
                  {
                    onSuccess: () => {
                      toast.success('Draft saved');
                      setEditing(false);
                    },
                    onError: (err) => setError(pfDigRefusal(err, 'The draft could not be saved.')),
                  },
                )
              }}
            >
              {update.isPending ? 'Saving…' : 'Save draft'}
            </button>
          </div>
        </div>
      )}
      {issuing && (
        <PfInlineConfirm
          testId="pf-report-issue-confirm"
          message="Issue this dig report? It gets its number, is signed with your name, and the dig is marked reported. An issued report cannot be changed."
          confirmLabel="Issue report"
          pending={issue.isPending}
          error={error}
          onCancel={() => { setIssuing(false); setError(null); }}
          onConfirm={() => {
            setError(null);
            issue.mutate(report.id, {
              onSuccess: (r) => {
                toast.success(`${r.data?.reportNumber ?? 'Report'} issued`);
                setIssuing(false);
              },
              onError: (err) => setError(pfDigRefusal(err, 'The report could not be issued.')),
            });
          }}
        />
      )}
      {deleting && (
        <PfInlineConfirm
          testId="pf-report-delete-confirm"
          message="Delete this draft report? The dig keeps its sheet; a new draft can be made later."
          confirmLabel="Delete draft"
          cancelLabel="Keep it"
          pending={del.isPending}
          error={error}
          onCancel={() => { setDeleting(false); setError(null); }}
          onConfirm={() =>
            del.mutate(report.id, {
              onSuccess: () => {
                toast.success('Draft deleted');
                setDeleting(false);
                if (showOpenLink === false) navigate('/pipeline-facility/dig-reports', { replace: true });
              },
              onError: (err) => setError(pfDigRefusal(err, 'The draft could not be deleted.')),
            })
          }
        />
      )}
      <div className="flex flex-wrap gap-2">
        <button type="button" className={PF_SECONDARY} onClick={openPdf} disabled={pdfBusy} data-testid="pf-report-pdf">
          <FileText className="size-4" aria-hidden="true" />
          {pdfBusy ? 'Making PDF…' : draft ? 'Open draft PDF' : 'Open PDF'}
        </button>
        {draft && canWrite && !editing && !issuing && (
          <>
            <button type="button" className={PF_SECONDARY} onClick={() => { setError(null); setTitle(report.title ?? ''); setComments(report.comments ?? ''); setEditing(true); }}>
              Edit draft
            </button>
            <button type="button" className={PF_SECONDARY} onClick={() => { setError(null); setDeleting(true); }}>
              Delete draft
            </button>
            <button type="button" className={PF_PRIMARY} onClick={() => { setError(null); setIssuing(true); }} data-testid="pf-report-issue">
              Issue report
            </button>
          </>
        )}
        {showOpenLink && (
          <Link className={PF_SECONDARY} to={`/pipeline-facility/dig-reports/${report.id}`}>
            Open report page
          </Link>
        )}
      </div>
    </div>
  );
}
